import { ParsedUrlQuery } from "querystring"
import { GetServerSidePropsContext, GetServerSidePropsResult } from "next"
import * as A from "fp-ts/Array"
import * as E from "fp-ts/Either"
import * as O from "fp-ts/Option"
import * as F from "fp-ts/function"
import * as T from "fp-ts/Task"
import * as TE from "fp-ts/TaskEither"
import { Either } from "fp-ts/Either"
import { sequenceS } from "fp-ts/Apply"
import {
    fetchPageContent,
    FetchPageContent,
    FetchPageContentError,
    FetchPageContentMandatory,
    FetchPageContentOptional,
} from "./sanity/fetch-page-content"
import { SanityQueryContent } from "./sanity/sanity-queries"
import { Locale } from "./format/localization"
import { WebAppConfig } from "./web-app-config"
import { defaultRollbarClientServer } from "./rollbar.setup.server"

const sequence = F.flow(sequenceS(E.Apply), TE.fromEither)

type DefaultSanityContentPageProps<T extends SanityQueryContent> = {
    sanityContent: SanityContent<T>
}

export type SanityContent<T extends SanityQueryContent> = FetchPageContentMandatory & Pick<FetchPageContentOptional, T>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never
export type SanityContentProps<T extends SanityQueryContent | void = void> = {
    sanityContent: UnionToIntersection<T extends void ? FetchPageContent : SanityContent<Exclude<T, void>>>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const notFound = (): GetServerSidePropsResult<any> => ({
    notFound: true,
})
const contentFound =
    <K extends SanityQueryContent>(_: readonly K[]) =>
    (response: SanityContent<K>): GetServerSidePropsResult<DefaultSanityContentPageProps<K>> => ({
        props: {
            sanityContent: response,
        },
    })

const cruiseCode = (context: GetServerSidePropsContext<ParsedUrlQuery>): Either<FetchPageContentError, string> =>
    F.pipe(
        O.fromNullable(context.params?.cruiseCode),
        O.chain(O.fromPredicate((x) => x !== "undefined")),
        O.chain((x) => (Array.isArray(x) ? A.head(x) : O.some(x))),
        E.fromOption(() => "not-found"),
    )
const locale = (context: GetServerSidePropsContext<ParsedUrlQuery>): Either<FetchPageContentError, Locale> =>
    F.pipe(
        Locale.strictType.decode(context.locale),
        E.mapLeft(() => "not-found"),
    )

const fetchPageContentTE =
    <T extends SanityQueryContent>(config: WebAppConfig, includes: readonly T[]) =>
    ({ cruiseCode, locale }: { cruiseCode: string; locale: Locale }) =>
        F.pipe(
            TE.tryCatch(
                () => fetchPageContent(config, cruiseCode, locale, includes),
                (e): FetchPageContentError => {
                    defaultRollbarClientServer?.error(
                        `Sanity query error for ${cruiseCode}.`,
                        e instanceof Error ? e : new Error(String(e)),
                    )
                    console.error(`Sanity query error for ${cruiseCode}.`, e)
                    return "generic-error"
                },
            ),
            TE.chainEitherK(F.identity),
        )

const getServerSidePropsWithDefaultSanityContent = async <SQC extends SanityQueryContent>(
    config: WebAppConfig,
    context: GetServerSidePropsContext<ParsedUrlQuery>,
    includes: readonly SQC[] = [],
): Promise<GetServerSidePropsResult<DefaultSanityContentPageProps<SQC>>> => {
    const program = F.pipe(
        sequence({
            cruiseCode: cruiseCode(context),
            locale: locale(context),
        }),
        TE.chain(fetchPageContentTE(config, includes)),
        T.map((x) => E.fold(notFound, contentFound(includes))(x)),
    )
    return await program()
}

export default getServerSidePropsWithDefaultSanityContent
